import { useParams, useMatch, Outlet, useResolvedPath, useLoaderData } from "react-router-dom";
import { Suspense, useState } from "react";
import { defer, Await } from "react-router-dom";

/*
  AnimalListSkeleton - Displayed while API data is loading.
*/
const AnimalListSkeleton = () => {
    const skeletonRows = Array.from({ length: 20 }, (_, i) => i + 1);

    return (
        <>
            <div className="flex gap-2 md:gap-10 border-b border-white border-opacity-50 p-3">
                <p className="grow basis-0 text-center font-bold hidden md:block">24h</p>
                <p className="grow basis-0 text-center font-bold hidden md:block">Position</p>
                <p className="grow basis-0 text-center font-bold block md:hidden">#</p>
                <p className="grow basis-0 text-center"></p>
                <p className="grow-[4] basis-0 font-bold">Name</p>
                <p className="grow basis-0 text-center font-bold">Rating</p>
            </div>
            {skeletonRows.map((position) => (
                <div key={position} className="flex gap-2 md:gap-10 border-b border-white border-opacity-20 p-3 animate-pulse">
                    <div className="grow basis-0 flex justify-center items-center">
                        <div className="w-6 h-6 bg-gray-700 rounded"></div>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <p className="text-gray-500">{position}</p>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <div className="w-16 h-16 bg-gray-700 rounded"></div>
                    </div>
                    <div className="grow-[4] basis-0 flex items-center">
                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <div className="h-4 bg-gray-700 rounded w-12"></div>
                    </div>
                </div>
            ))}
        </>
    );
};

/*
  AnimalRow - Renders a single row with progressive image loading.
*/
const AnimalRow = ({ animal, animalImagePath }) => {
    const [imageLoaded, setImageLoaded] = useState(false);

    return (
        <div className="flex gap-2 md:gap-10 border-b border-white border-opacity-20 p-3">
            <div className="grow basis-0 flex justify-center items-center gap-1">
                {animal.currentPosition < animal.previousPosition ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="#4ade80"
                        className="w-6 h-6"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                    </svg>
                ) : (
                    animal.previousPosition < animal.currentPosition && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="#f87171"
                            className="w-6 h-6"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                        </svg>
                    )
                )}
                {
                    animal.currentPosition - animal.previousPosition > 0 ? (
                        <p className="text-red-400">{Math.abs(animal.previousPosition - animal.currentPosition)}</p>
                    ) : animal.currentPosition - animal.previousPosition < 0 ? (
                        <p className="text-green-400">{Math.abs(animal.currentPosition - animal.previousPosition)}</p>
                    ) : (
                        <p></p>
                    )
                }
            </div>

            <div className="grow basis-0 flex justify-center items-center">
                <p className="">
                    {animal.position === 1 ?
                        <svg height="30" viewBox="0 0 441 430" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M295 200.5H147L295 0H441L295 200.5Z" fill="#FB6767"/>
                            <path d="M148 0H0L148 200.5H294L148 0Z" fill="#FD9E9E"/>
                            <circle cx="224.5" cy="295.5" r="134.5" fill="#EED15C"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M224 399C281.438 399 328 352.438 328 295C328 237.562 281.438 191 224 191C166.562 191 120 237.562 120 295C120 352.438 166.562 399 224 399ZM224 375C268.183 375 304 339.183 304 295C304 250.817 268.183 215 224 215C179.817 215 144 250.817 144 295C144 339.183 179.817 375 224 375Z" fill="#D9BB44"/>
                        </svg>
                    : animal.position === 2 ?
                        <svg height="30" viewBox="0 0 441 430" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M295 200.5H147L295 0H441L295 200.5Z" fill="#FB6767"/>
                            <path d="M148 0H0L148 200.5H294L148 0Z" fill="#FD9E9E"/>
                            <circle cx="224.5" cy="295.5" r="134.5" fill="#D9D9D9"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M224 399C281.438 399 328 352.438 328 295C328 237.562 281.438 191 224 191C166.562 191 120 237.562 120 295C120 352.438 166.562 399 224 399ZM224 375C268.183 375 304 339.183 304 295C304 250.817 268.183 215 224 215C179.817 215 144 250.817 144 295C144 339.183 179.817 375 224 375Z" fill="#B9B7B7"/>
                        </svg>
                    : animal.position === 3 ?
                        <svg height="30" viewBox="0 0 441 430" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M295 200.5H147L295 0H441L295 200.5Z" fill="#FB6767"/>
                            <path d="M148 0H0L148 200.5H294L148 0Z" fill="#FD9E9E"/>
                            <circle cx="224.5" cy="295.5" r="134.5" fill="#D7961A"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M224 399C281.438 399 328 352.438 328 295C328 237.562 281.438 191 224 191C166.562 191 120 237.562 120 295C120 352.438 166.562 399 224 399ZM224 375C268.183 375 304 339.183 304 295C304 250.817 268.183 215 224 215C179.817 215 144 250.817 144 295C144 339.183 179.817 375 224 375Z" fill="#B7872B"/>
                        </svg>
                    : animal.position}
                </p>
            </div>
            <div className="grow basis-0 flex justify-center items-center h-20">
                {imageLoaded ? (
                    <img
                        className="h-20 object-contain"
                        src={`${animalImagePath}/${animal.animal_id}.png`}
                        alt={animal.animal_id}
                    />
                ) : (
                    <>
                        <div className="w-16 h-16 bg-gray-700 rounded animate-pulse"></div>
                        <img
                            className="h-0 w-0"
                            src={`${animalImagePath}/${animal.animal_id}.png`}
                            alt=""
                            onLoad={() => setImageLoaded(true)}
                        />
                    </>
                )}
            </div>
            <div className="grow-[4] basis-0 flex items-center">
                <p className="">{animal.animal_short_name} <span className="hidden md:inline-block">({animal.animal_id})</span></p>
            </div>
            <div className="grow basis-0 flex justify-center items-center">
                <p className="">{Math.round(animal.rating)}</p>
            </div>
        </div>
    );
};

// Component to render the animal list once data is loaded
const AnimalList = ({ animals, animalImagePath }) => {
    return (
        <>
            <div className="flex gap-2 md:gap-10 border-b border-white border-opacity-50 p-3">
                <p className="grow basis-0 text-center font-bold hidden md:block">24h</p>
                <p className="grow basis-0 text-center font-bold hidden md:block">Position</p>
                <p className="grow basis-0 text-center font-bold block md:hidden">#</p>
                <p className="grow basis-0 text-center"></p>
                <p className="grow-[4] basis-0 font-bold">Name</p>
                <p className="grow basis-0 text-center font-bold">Rating</p>
            </div>

            {animals.map((animal) => (
                <AnimalRow
                    key={animal.id}
                    animal={animal}
                    animalImagePath={animalImagePath}
                />
            ))}
        </>
    );
};

const AnimalResultsPage = ({animalThemesData}) => {
    const {themeUrl} = useParams();
    const theme = animalThemesData.find((themeData) => themeData.url === themeUrl);
    const themeName = theme ? theme.theme_name : "";

    const url = useResolvedPath("").pathname;
    const match = useMatch('/results/animals/:themeUrl');
    const animalImagePath = "/images/animals";

    const data = useLoaderData();

    return (
        <div>
            {match && (
            <div className="container mx-auto text-sm md:text-md lg:text-lg">

                <div className="flex justify-center py-5">
                    <h1 className="text-2xl font-bold text-center">The 100 Highest Rated LEGO {themeName} Animals</h1>
                </div>

                <Suspense fallback={<AnimalListSkeleton />}>
                    <Await resolve={data.animals}>
                        {(animals) => (
                            <AnimalList
                                animals={animals}
                                animalImagePath={animalImagePath}
                            />
                        )}
                    </Await>
                </Suspense>

            </div>
            )}

            <Outlet />
        </div>
    );
}

export default AnimalResultsPage;

export const topAnimalsByThemeLoader = async ({ params }) => {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    return defer({
        animals: fetch(`${apiUrl}/getTopAnimalsByTheme.php?themeUrl=${params.themeUrl}`)
            .then(res => res.json())
    });
};
